import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Link, Navigate } from "react-router-dom";
import { LayoutDashboard, Cpu, Boxes, Package, Tags, Shapes, ListTree, FileText, Users as UsersIcon, Settings as Cog, Plus, Edit, Trash2, Upload, Download, File as FileIcon, Sparkles, Wand2, ImagePlus, Image as ImageIcon, Search, Compass, BookOpen, Banknote } from "lucide-react";
import { toast } from "sonner";
import ImageManager from "./admin/ImageManager";
import KioskInspector from "./admin/KioskInspector";
import UseCaseMapper from "./admin/UseCaseMapper";
import BlogManager from "./admin/BlogManager";

const TABS = [
  { k: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { k: "kiosks", label: "Kiosk Inspector", icon: Search },
  { k: "usecase-mapper", label: "Gebruikssituaties", icon: Compass },
  { k: "blog", label: "Blog & SEO", icon: BookOpen },
  { k: "subsidies", label: "Subsidies", icon: Banknote, collection: "subsidies" },
  { k: "hardware", label: "Hardware", icon: Cpu, collection: "hardware" },
  { k: "images", label: "Foto Manager", icon: ImageIcon },
  { k: "software", label: "Software", icon: Boxes, collection: "software" },
  { k: "addons", label: "Add-ons", icon: Package, collection: "addons" },
  { k: "bundles", label: "Bundels", icon: Tags, collection: "bundles" },
  { k: "vendors", label: "Leveranciers", icon: Shapes, collection: "vendors" },
  { k: "sectors", label: "Sectoren", icon: ListTree, collection: "sectors" },
  { k: "usecases", label: "Toepassingen", icon: ListTree, collection: "usecases" },
  { k: "partners", label: "Partners", icon: Shapes, collection: "partners" },
  { k: "pricelists", label: "Prijslijsten", icon: Upload },
  { k: "ai", label: "AI Studio", icon: Wand2 },
  { k: "quotes", label: "Offertes", icon: FileText },
  { k: "payments", label: "Betalingen", icon: Banknote },
  { k: "users", label: "Gebruikers", icon: UsersIcon },
  { k: "settings", label: "Instellingen", icon: Cog },
];

export default function AdminPage() {
  const { user, authLoading } = useApp();
  const [tab, setTab] = useState("dashboard");

  if (authLoading) return <div className="container-x py-20 text-[#A8B3AD]">Laden…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" />;

  return (
    <div className="container-x py-10" data-testid="admin-page">
      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="card-surface p-3 sticky top-24">
            <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] px-3 py-2">Admin CMS</div>
            <nav className="space-y-1">
              {TABS.map((tabItem) => {
                const Icon = tabItem.icon;
                return (
                  <button key={tabItem.k} onClick={() => setTab(tabItem.k)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition ${tab === tabItem.k ? "bg-[#1F3A2E] text-white" : "text-[#A8B3AD] hover:bg-[#1A2421]"}`}
                    data-testid={`admin-tab-${tabItem.k}`}>
                    <Icon className="w-4 h-4" /> {tabItem.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        <div className="lg:col-span-9">
          {tab === "dashboard" && <Dashboard />}
          {tab === "kiosks" && <KioskInspector />}
          {tab === "usecase-mapper" && <UseCaseMapper />}
          {tab === "blog" && <BlogManager />}
          {tab === "quotes" && <QuotesTable />}
          {tab === "payments" && <Payments />}
          {tab === "users" && <UsersTable />}
          {tab === "settings" && <Settings />}
          {tab === "pricelists" && <Pricelists />}
          {tab === "ai" && <AIStudio />}
          {tab === "images" && <ImageManager />}
          {TABS.filter((tt) => tt.collection).map((tt) => (
            tab === tt.k && <CrudTable key={tt.k} resource={tt.collection} label={tt.label} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Payments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/payments").then((r) => setRows(r.data || [])).finally(() => setLoading(false)); }, []);
  const fmt = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(n || 0);
  const badge = (s) => {
    const map = { paid: "bg-[#1F3A2E] text-[#8FE0B4]", pending: "bg-[#3A331F] text-[#E0C98F]", failed: "bg-[#3A1F1F] text-[#E08F8F]", expired: "bg-[#2A2A2A] text-[#A8B3AD]" };
    return map[s] || "bg-[#2A2A2A] text-[#A8B3AD]";
  };
  return (
    <div data-testid="admin-payments">
      <h2 className="display text-3xl font-semibold">Betalingen</h2>
      <p className="text-[#A8B3AD] text-sm mb-6">Overzicht van alle Stripe-betalingen, gekoppeld aan offerte en gebruiker.</p>
      {loading ? <div className="text-[#A8B3AD]">Laden…</div> : rows.length === 0 ? (
        <div className="card-surface p-8 text-center text-[#A8B3AD]">Nog geen betalingen.</div>
      ) : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-payments-table">
            <thead>
              <tr className="text-left text-[#7A8580] border-b border-white/5">
                <th className="px-4 py-3">Offerte</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Bedrag</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Datum</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.session_id} className="border-b border-white/5" data-testid={`payment-row-${r.session_id}`}>
                  <td className="px-4 py-3 font-medium text-[#F4F1EA]">{r.quote_number || r.quote_id || "-"}</td>
                  <td className="px-4 py-3">{r.plan === "deposit" ? "Aanbetaling 50%" : "Volledig"}</td>
                  <td className="px-4 py-3 font-semibold">{fmt(r.amount)}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 rounded-md text-xs font-semibold ${badge(r.payment_status)}`}>{r.payment_status}</span></td>
                  <td className="px-4 py-3 text-[#A8B3AD]">{(r.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div>Laden…</div>;
  return (
    <div>
      <h2 className="display text-3xl font-semibold">Overzicht</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
        {Object.entries(stats).map(([k, v]) => (
          <div key={k} className="card-surface p-5" data-testid={`stat-${k}`}>
            <div className="text-xs uppercase tracking-widest text-[#A8B3AD]">{k}</div>
            <div className="display text-3xl font-semibold text-[#6FAA8A] mt-1">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CrudTable({ resource, label }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/admin/${resource}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [resource]);

  const save = async () => {
    try {
      let payload = JSON.parse(editing.json);
      if (editing.id) await api.put(`/admin/${resource}/${editing.id}`, payload);
      else await api.post(`/admin/${resource}`, payload);
      toast.success("Opgeslagen");
      setEditing(null);
      load();
    } catch (e) { toast.error("Opslaan mislukt - check JSON"); }
  };

  const del = async (id) => {
    if (!window.confirm("Verwijderen?")) return;
    await api.delete(`/admin/${resource}/${id}`);
    load();
  };

  const newItem = () => {
    const template = (resource === "hardware") ?
      { sku: "NEW-SKU", vendor_slug: "qiox", name: "New hardware", type: "counter", placement: "indoor", purchase_price: 1000, lease_price_36: 35, lease_price_48: 28, lease_price_60: 24, sector_slugs: [], usecase_slugs: [], description_i18n: {nl: "", en: "", de: "", fr: ""}, active: true }
      : (resource === "software") ?
      { slug: "new-software", name: "New software", pricing_model: "monthly", price_monthly: 39, sector_slugs: [], usecase_slugs: [], description_i18n: {nl:"",en:"",de:"",fr:""}, icon: "Boxes", active: true }
      : (resource === "addons") ?
      { sku: "NEW-ADDON", name: "New add-on", category: "other", placement: "both", price: 50, net_price: 30, image_url: null, function_i18n: {nl:"",en:"",de:"",fr:""}, description_i18n: {nl:"",en:"",de:"",fr:""}, active: true, vendor_slug: null }
      : (resource === "bundles") ?
      { slug: "new-bundle", name_i18n: {nl:"",en:"",de:"",fr:""}, description_i18n: {nl:"",en:"",de:"",fr:""}, sector_slugs: [], usecase_slugs: [], hardware_ids: [], software_ids: [], addon_ids: [], discount_pct: 5, is_recommended: true }
      : (resource === "vendors") ?
      { name: "New vendor", slug: "new-vendor", description: "" }
      : (resource === "partners") ? {
        slug: "new-partner", name: "New Partner",
        tagline_nl: "",
        hero_title_nl: "", hero_subtitle_nl: "", hero_cta_nl: "Start",
        show_vendor_names: false,
        logo_url: "", logo_on_dark: false,
        primary_color: "#12B76A", accent_color: "#FF8D40",
        theme_mode: "light",
        allowed_software_slugs: [],
        allowed_sector_slugs: ["hospitality", "leisure", "dagrecreatie"],
        contact_email: "",
        footer_brand: "",
        is_default: false,
        badge: "Certified Partner",
        hero_image_url: "",
        profile_description: "",
        website_url: "",
        blog_url: "",
        stats: [
          {icon: "Users",   value: "0+",  label: "Locaties"},
          {icon: "Clock",   value: "0+ jr", label: "Ervaring"},
          {icon: "Package", value: "0+",  label: "Kiosken live"},
          {icon: "Star",    value: "0/5", label: "NPS"},
        ],
        services: [
          {icon: "PlugZap", title: "PMS-koppeling", description: ""},
          {icon: "LogIn",   title: "Self check-in", description: ""},
        ],
        testimonial: {quote: "", author: "", role: "", initials: ""},
        featured_content: {kind: "case", overline: "KLANTCASE", title: "", body: "", cta_label: "Lees meer", cta_url: ""},
        top_hardware_skus: [],
        active: true,
      }
      : { slug: "new", name_i18n: {nl:"",en:"",de:"",fr:""}, description_i18n: {nl:"",en:"",de:"",fr:""}, icon: "Circle", order: 99, sector_slugs: [] };
    setEditing({ json: JSON.stringify(template, null, 2), id: null });
  };

  return (
    <div data-testid={`admin-crud-${resource}`}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="display text-3xl font-semibold">{label}</h2>
        <button className="btn-accent" onClick={newItem} data-testid={`admin-${resource}-new`}>
          <Plus className="w-4 h-4" /> Nieuw
        </button>
      </div>

      {editing && (
        <div className="card-surface p-5 mb-6" data-testid={`admin-${resource}-editor`}>
          <div className="font-semibold mb-3">{editing.id ? "Bewerken" : "Nieuw"}</div>
          <textarea className="input-field font-mono text-xs" rows={18} value={editing.json} onChange={(e) => setEditing({ ...editing, json: e.target.value })} data-testid={`admin-${resource}-json`} />
          <div className="flex gap-2 mt-3">
            <button className="btn-primary" onClick={save} data-testid={`admin-${resource}-save`}>Opslaan</button>
            <button className="btn-ghost" onClick={() => setEditing(null)} data-testid={`admin-${resource}-cancel`}>Annuleer</button>
          </div>
        </div>
      )}

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#1F2A26] text-[#A8B3AD] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Naam / ID</th>
              <th className="text-left px-4 py-3">Details</th>
              <th className="text-right px-4 py-3">Acties</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-[#243430]">
                <td className="px-4 py-3 font-semibold text-[#F4F1EA]">{it.name || it.name_i18n?.nl || it.sku || it.slug}</td>
                <td className="px-4 py-3 text-xs text-[#A8B3AD] max-w-md truncate">{it.sku || it.slug || ""} {it.vendor_slug ? `· ${it.vendor_slug}` : ""} {it.purchase_price ? `· €${it.purchase_price}` : ""}</td>
                <td className="px-4 py-3 text-right">
                  <button className="btn-ghost !py-1 !px-2" onClick={() => setEditing({ id: it.id, json: JSON.stringify(it, null, 2) })} data-testid={`admin-${resource}-edit-${it.id}`}>
                    <Edit className="w-4 h-4" />
                  </button>
                  <button className="btn-ghost !py-1 !px-2 text-[#FF6B5B]" onClick={() => del(it.id)} data-testid={`admin-${resource}-delete-${it.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function QuotesTable() {
  const { formatPrice } = useApp();
  const [quotes, setQuotes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ status: "", q: "" });
  const load = () => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.q) params.set("q", filters.q);
    const qs = params.toString();
    return api.get(`/admin/leads${qs ? "?" + qs : ""}`).then((r) => setQuotes(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters.status]);
  const update = async (id, patch) => { const { data } = await api.patch(`/admin/leads/${id}`, patch); load(); if (selected?.id === id) setSelected(data); };

  const STATUS_CHIP = {
    new: "bg-[#D97742]/20 text-[#D97742]",
    contacted: "bg-[#6FAA8A]/20 text-[#6FAA8A]",
    qualified: "bg-[#6FAA8A]/30 text-[#6FAA8A]",
    won: "bg-green-500/20 text-green-400",
    lost: "bg-red-500/20 text-red-400",
  };

  const [partners, setPartners] = React.useState([]);
  React.useEffect(() => { api.get("/partners").then((r) => setPartners(r.data)).catch(() => {}); }, []);
  const [exportPartner, setExportPartner] = React.useState("");
  const downloadCsv = () => {
    const params = new URLSearchParams();
    if (exportPartner) params.set("partner_slug", exportPartner);
    // Current month by default
    const d = new Date();
    const start = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
    params.set("since", start);
    params.set("until", end);
    window.open(`${API}/admin/leads/export?${params.toString()}`, "_blank");
  };

  return (
    <div data-testid="admin-leads-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="display text-3xl font-semibold">Leads &amp; offertes</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="input-field !py-1.5 max-w-[200px]"
            value={exportPartner}
            onChange={(e) => setExportPartner(e.target.value)}
            data-testid="leads-export-partner">
            <option value="">Alle partners</option>
            {partners.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
          <button className="btn-outline" onClick={downloadCsv} data-testid="leads-export-csv">
            Export CSV (deze maand)
          </button>
          <div className="text-sm text-[#A8B3AD]" data-testid="leads-count">{quotes.length} items</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-surface p-4 flex flex-wrap gap-3 mb-4">
        <input
          className="input-field max-w-xs"
          placeholder="Zoek op bedrijf / e-mail / offertenr"
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="leads-search"
        />
        <select
          className="input-field max-w-[180px]"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          data-testid="leads-status-filter">
          <option value="">Alle statussen</option>
          <option value="new">Nieuw</option>
          <option value="contacted">Gebeld</option>
          <option value="qualified">Gekwalificeerd</option>
          <option value="won">Gewonnen</option>
          <option value="lost">Verloren</option>
        </select>
        <button className="btn-outline" onClick={load} data-testid="leads-refresh">Zoek</button>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Table */}
        <div className={`card-surface overflow-hidden ${selected ? "lg:col-span-3" : "lg:col-span-5"}`}>
          <table className="w-full text-sm">
            <thead className="bg-[#1F2A26] text-[#A8B3AD] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Datum</th>
                <th className="text-left px-4 py-3">Offerte</th>
                <th className="text-left px-4 py-3">Bedrijf</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Land</th>
                <th className="text-left px-4 py-3">Totaal</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id}
                    onClick={() => setSelected(q)}
                    className={`border-t border-[#243430] hover:bg-[#1A2421] cursor-pointer ${selected?.id === q.id ? "bg-[#1A2421]" : ""}`}
                    data-testid={`lead-row-${q.id}`}>
                  <td className="px-4 py-3 text-[#A8B3AD]">{(q.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 font-semibold">{q.quote_number}</td>
                  <td className="px-4 py-3">{q.company_name}</td>
                  <td className="px-4 py-3 text-xs">{q.contact_name}<br /><span className="text-[#A8B3AD]">{q.contact_email}</span></td>
                  <td className="px-4 py-3">{q.country || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatPrice(q.total_once || q.subtotal_once)}<br /><span className="text-xs text-[#D97742]">+{formatPrice(q.subtotal_monthly)}/mnd</span></td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_CHIP[q.status] || STATUS_CHIP.new}`}>{q.status}</span>
                  </td>
                </tr>
              ))}
              {quotes.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#A8B3AD]">Nog geen leads</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Detail panel */}
        {selected && (
          <aside className="card-surface p-5 lg:col-span-2 h-fit sticky top-4" data-testid="lead-detail-panel">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs text-[#6FAA8A] uppercase tracking-wider font-semibold">Lead</div>
                <div className="text-2xl font-bold text-[#F4F1EA]">{selected.quote_number}</div>
              </div>
              <button className="btn-ghost !p-2" onClick={() => setSelected(null)} data-testid="lead-close">✕</button>
            </div>

            <div className="space-y-2 text-sm">
              <Row k="Bedrijf" v={selected.company_name} />
              <Row k="Contact" v={selected.contact_name} />
              <Row k="E-mail" v={<a className="text-[#6FAA8A] underline" href={`mailto:${selected.contact_email}`}>{selected.contact_email}</a>} />
              <Row k="Telefoon" v={selected.contact_phone ? <a className="text-[#6FAA8A]" href={`tel:${selected.contact_phone}`}>{selected.contact_phone}</a> : "—"} />
              <Row k="Adres" v={[selected.address_street, selected.address_postal, selected.address_city, selected.country].filter(Boolean).join(", ") || "—"} />
              <Row k="Installatie" v={selected.install_mode === "install" ? `Laten installeren (${formatPrice(selected.install_fee || 0)})` : `Zelf installeren (${formatPrice(selected.shipping_fee || 0)})`} />
              <Row k="Sector" v={selected.sector_slug || "—"} />
              <Row k="Partner" v={selected.partner_slug || "Kiosk"} />
            </div>

            <div className="mt-4 pt-4 border-t border-white/5">
              <div className="text-xs text-[#6FAA8A] uppercase tracking-wider font-semibold mb-2">Items ({selected.items?.length || 0})</div>
              <ul className="space-y-1 text-xs">
                {(selected.items || []).map((it, i) => (
                  <li key={i} className="flex justify-between">
                    <span>{it.quantity || 1}× {it.name}</span>
                    <span className="text-[#A8B3AD]">{it.sku || it.type}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
              <label className="block">
                <span className="text-xs text-[#6FAA8A] uppercase tracking-wider font-semibold">Status</span>
                <select
                  className="input-field mt-1"
                  value={selected.status || "new"}
                  onChange={(e) => update(selected.id, { status: e.target.value })}
                  data-testid="lead-status-select">
                  <option value="new">Nieuw</option>
                  <option value="contacted">Gebeld</option>
                  <option value="qualified">Gekwalificeerd</option>
                  <option value="won">Gewonnen</option>
                  <option value="lost">Verloren</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-[#6FAA8A] uppercase tracking-wider font-semibold">Notities (intern)</span>
                <textarea
                  className="input-field mt-1"
                  rows={4}
                  defaultValue={selected.sales_notes || ""}
                  onBlur={(e) => update(selected.id, { sales_notes: e.target.value })}
                  placeholder="Gebeld op 14 mei, gespecificeerd voor 3 campings. Reageert volgende week."
                  data-testid="lead-notes"
                />
              </label>
              <a href={`${API}/quotes/${selected.id}/pdf`} target="_blank" rel="noreferrer" className="btn-outline w-full justify-center" data-testid="lead-pdf">
                PDF downloaden
              </a>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex gap-3">
    <div className="w-24 shrink-0 text-[11px] text-[#A8B3AD] uppercase tracking-wider pt-0.5">{k}</div>
    <div className="flex-1 text-[#F4F1EA]">{v}</div>
  </div>
);

function UsersTable() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  return (
    <div data-testid="admin-users-tab">
      <h2 className="display text-3xl font-semibold mb-5">Gebruikers</h2>
      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#1F2A26] text-[#A8B3AD] text-xs uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">E-mail</th><th className="text-left px-4 py-3">Naam</th><th className="text-left px-4 py-3">Rol</th><th className="text-left px-4 py-3">Bevestigd</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#243430]">
                <td className="px-4 py-3 font-semibold">{u.email}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3">{u.role}</td>
                <td className="px-4 py-3">{u.email_verified ? "✓" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Settings() {
  const [rates, setRates] = useState({ EUR: 1.0, USD: 1.10, GBP: 0.86, CHF: 0.96 });
  const [lease, setLease] = useState({
    lease_factor_monthly: 0.0219, fee_percentage: 0.10, fee_minimum: 500,
    service_fee_monthly: 0, default_contract_duration: 60, rounding: 2,
  });
  const [leaseLoaded, setLeaseLoaded] = useState(false);
  const [leaseSaving, setLeaseSaving] = useState(false);

  useEffect(() => {
    api.get("/currency/rates").then((r) => r.data?.rates && setRates(r.data.rates));
    api.get("/lease/settings").then((r) => { setLease((prev) => ({ ...prev, ...r.data })); setLeaseLoaded(true); });
  }, []);
  const save = async () => {
    try { await api.put("/admin/currency/rates", { rates }); toast.success("Rates opgeslagen"); }
    catch { toast.error("Fout"); }
  };
  const saveLease = async () => {
    setLeaseSaving(true);
    try {
      const payload = {
        lease_factor_monthly: Number(lease.lease_factor_monthly),
        fee_percentage: Number(lease.fee_percentage),
        fee_minimum: Number(lease.fee_minimum),
        service_fee_monthly: Number(lease.service_fee_monthly || 0),
        default_contract_duration: Number(lease.default_contract_duration),
        rounding: Number(lease.rounding),
      };
      const { data } = await api.patch("/admin/lease/settings", payload);
      setLease((prev) => ({ ...prev, ...data }));
      toast.success("Lease-parameters opgeslagen");
    } catch { toast.error("Kon niet opslaan"); }
    finally { setLeaseSaving(false); }
  };

  // Preview the impact live
  const preview = (inv) => {
    const fee = Math.max(inv * lease.fee_percentage, lease.fee_minimum);
    const monthly = (inv + fee) * lease.lease_factor_monthly + Number(lease.service_fee_monthly || 0);
    return Math.round(monthly * 100) / 100;
  };

  return (
    <div data-testid="admin-settings-tab" className="space-y-8">
      <div>
        <h2 className="display text-3xl font-semibold mb-5">Instellingen · Valuta</h2>
        <div className="card-surface p-6 max-w-md space-y-3">
          {Object.entries(rates).map(([code, val]) => (
            <label key={code} className="flex items-center gap-3">
              <span className="w-16 font-semibold text-[#6FAA8A]">{code}</span>
              <input type="number" step="0.01" className="input-field" value={val}
                onChange={(e) => setRates({ ...rates, [code]: parseFloat(e.target.value || "0") })}
                data-testid={`admin-rate-${code}`} disabled={code === "EUR"} />
            </label>
          ))}
          <button className="btn-primary" onClick={save} data-testid="admin-rates-save">Opslaan</button>
        </div>
      </div>

      <div>
        <h2 className="display text-3xl font-semibold mb-2">Instellingen · Lease &amp; Fee Engine</h2>
        <p className="text-sm text-[#A8B3AD] mb-5 max-w-2xl">
          Deterministische formule, alle &ldquo;vanaf € / maand&rdquo; prijzen op de site en in offertes komen hier vandaan.
          Wijzigingen zijn direct actief voor alle nieuwe configuraties.
        </p>
        <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
          <div className="card-surface p-6 space-y-4">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#A8B3AD]">Lease factor (maand)</span>
              <input data-testid="admin-lease-factor" type="number" step="0.0001" className="input-field mt-1" value={lease.lease_factor_monthly}
                onChange={(e) => setLease({ ...lease, lease_factor_monthly: e.target.value })} />
              <span className="text-[11px] text-[#A8B3AD] mt-1 block">Grenke benchmark: 0.0219</span>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#A8B3AD]">Fee percentage</span>
              <input data-testid="admin-lease-fee-pct" type="number" step="0.01" className="input-field mt-1" value={lease.fee_percentage}
                onChange={(e) => setLease({ ...lease, fee_percentage: e.target.value })} />
              <span className="text-[11px] text-[#A8B3AD] mt-1 block">0.10 = 10% marge op investering</span>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#A8B3AD]">Fee minimum (€)</span>
              <input data-testid="admin-lease-fee-min" type="number" step="10" className="input-field mt-1" value={lease.fee_minimum}
                onChange={(e) => setLease({ ...lease, fee_minimum: e.target.value })} />
              <span className="text-[11px] text-[#A8B3AD] mt-1 block">Ondergrens zodat kleine deals kosten dekken</span>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#A8B3AD]">Service fee / maand (€, optioneel)</span>
              <input data-testid="admin-lease-service" type="number" step="1" className="input-field mt-1" value={lease.service_fee_monthly}
                onChange={(e) => setLease({ ...lease, service_fee_monthly: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#A8B3AD]">Standaard looptijd (maanden)</span>
              <select data-testid="admin-lease-duration" className="input-field mt-1" value={lease.default_contract_duration}
                onChange={(e) => setLease({ ...lease, default_contract_duration: parseInt(e.target.value, 10) })}>
                <option value={36}>36</option>
                <option value={48}>48</option>
                <option value={60}>60</option>
              </select>
            </label>
            <button data-testid="admin-lease-save" className="btn-primary" disabled={!leaseLoaded || leaseSaving} onClick={saveLease}>
              {leaseSaving ? "Opslaan..." : "Lease-parameters opslaan"}
            </button>
          </div>

          <div className="card-surface p-6">
            <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A] mb-4">Live preview</div>
            <div className="space-y-3 text-sm">
              {[2500, 5000, 10000, 20000, 50000].map((inv) => (
                <div key={inv} className="flex items-baseline justify-between border-b border-[#243430] pb-2">
                  <span className="text-[#A8B3AD]">Investering € {inv.toLocaleString("nl-NL")}</span>
                  <span className="display text-lg font-semibold text-[#6FAA8A]" data-testid={`admin-lease-preview-${inv}`}>
                    € {preview(inv).toLocaleString("nl-NL")} / mnd
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-5 p-4 bg-[#141C19] rounded text-[11px] text-[#A8B3AD] leading-relaxed font-mono">
              monthly = (investment + max(investment × {lease.fee_percentage}, €{lease.fee_minimum})) × {lease.lease_factor_monthly}
              {Number(lease.service_fee_monthly) > 0 && ` + €${lease.service_fee_monthly}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function Pricelists() {
  const [lists, setLists] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [file, setFile] = useState(null);
  const [vendor, setVendor] = useState("qiox");
  const [label, setLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = () => api.get("/admin/pricelists").then((r) => setLists(r.data));
  useEffect(() => { load(); api.get("/admin/vendors").then((r) => { setVendors(r.data); if (r.data[0]) setVendor(r.data[0].slug); }); }, []);

  const upload = async () => {
    if (!file) return toast.error("Kies een bestand");
    const CHUNK_SIZE = 512 * 1024; // 512KB chunks bypass proxy body-size limits
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setUploading(true);
    setProgress(0);
    try {
      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", String(i));
        fd.append("chunk", blob, file.name);
        await api.post("/admin/pricelists/chunk", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      await api.post("/admin/pricelists/finalize", {
        upload_id: uploadId,
        filename: file.name,
        vendor_slug: vendor,
        label,
        total_chunks: totalChunks,
      }, { timeout: 120000 });
      toast.success("Prijslijst geüpload");
      setFile(null); setLabel("");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "onbekende fout";
      toast.error("Upload mislukt: " + detail);
    }
    finally { setUploading(false); setProgress(0); }
  };

  const del = async (id) => {
    if (!window.confirm("Verwijder prijslijst?")) return;
    await api.delete(`/admin/pricelists/${id}`);
    load();
  };

  const download = async (id, filename) => {
    const r = await api.get(`/admin/pricelists/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-pricelists-tab">
      <h2 className="display text-3xl font-semibold mb-2">Prijslijsten</h2>
      <p className="text-[#A8B3AD] text-sm mb-6">Upload nieuwe prijslijsten (PDF/CSV/XLSX/Numbers) per leverancier. Je kunt ze daarna downloaden of verwijderen. Nieuwe product-data kan je handmatig verwerken in de Hardware-tab.</p>

      <div className="card-surface p-6 mb-8" data-testid="admin-pricelist-upload">
        <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A] mb-4">Nieuwe prijslijst uploaden</div>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#6FAA8A]">Leverancier</span>
            <select className="input-field mt-1" value={vendor} onChange={(e) => setVendor(e.target.value)} data-testid="pricelist-vendor-select">
              {vendors.map((v) => <option key={v.slug} value={v.slug}>{v.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#6FAA8A]">Label / periode</span>
            <input className="input-field mt-1" placeholder="bv. Q3 2025" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="pricelist-label-input" />
          </label>
        </div>
        <div className="mt-4">
          <label className="block cursor-pointer">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#6FAA8A]">Bestand</span>
            <div className="mt-1 flex items-center gap-3 border border-dashed border-[#2E3E39] rounded-lg p-6 hover:border-[#6FAA8A] transition">
              <Upload className="w-5 h-5 text-[#6FAA8A]" />
              <input type="file" accept=".pdf,.csv,.xlsx,.numbers"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="pricelist-file-input" />
              {file && <span className="text-sm text-[#F4F1EA] ml-auto">{file.name} ({Math.round(file.size / 1024)} KB)</span>}
            </div>
          </label>
        </div>
        <button className="btn-accent mt-5" onClick={upload} disabled={uploading || !file} data-testid="pricelist-upload-button">
          <Upload className="w-4 h-4" /> {uploading ? `Uploaden… ${progress}%` : "Upload prijslijst"}
        </button>
      </div>

      <div className="card-surface overflow-hidden">
        <table className="luxe" data-testid="admin-pricelist-table">
          <thead>
            <tr><th>Leverancier</th><th>Label</th><th>Bestand</th><th>Grootte</th><th>Geüpload</th><th className="!text-right">Acties</th></tr>
          </thead>
          <tbody>
            {lists.length === 0 && <tr><td colSpan={6} className="text-center text-[#A8B3AD] py-10">Nog geen prijslijsten geüpload.</td></tr>}
            {lists.map((p) => (
              <tr key={p.id}>
                <td className="font-semibold text-[#6FAA8A]">{p.vendor_slug}</td>
                <td>{p.label}</td>
                <td className="flex items-center gap-2 text-[#F4F1EA]"><FileIcon className="w-4 h-4 text-[#6FAA8A]" /> {p.filename}</td>
                <td className="text-[#A8B3AD]">{Math.round((p.size_bytes || 0) / 1024)} KB</td>
                <td className="text-[#A8B3AD] text-xs">{new Date(p.uploaded_at).toLocaleString()}</td>
                <td className="!text-right">
                  <button className="btn-ghost !py-1 !px-2" onClick={() => download(p.id, p.filename)} data-testid={`pricelist-download-${p.id}`}>
                    <Download className="w-4 h-4" />
                  </button>
                  <button className="btn-ghost !py-1 !px-2 text-[#FF6B5B]" onClick={() => del(p.id)} data-testid={`pricelist-delete-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AIStudio() {
  const { user } = useApp();
  const [hardware, setHardware] = useState([]);
  const [hwId, setHwId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [refFile, setRefFile] = useState(null);
  const [saveToHardware, setSaveToHardware] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const BASE = process.env.REACT_APP_BACKEND_URL;

  const loadHistory = () => api.get("/admin/ai/generated").then((r) => setHistory(r.data));
  useEffect(() => {
    api.get("/admin/hardware").then((r) => setHardware(r.data));
    loadHistory();
  }, []);

  const fillFromHardware = (id) => {
    setHwId(id);
    const h = hardware.find((x) => x.id === id);
    if (h) {
      const desc = h.description_i18n?.nl || h.description_i18n?.en || "";
      setPrompt(`${h.name}, ${h.type} ${h.placement} kiosk. ${desc}`);
    }
  };

  const generate = async () => {
    if (!prompt.trim() || prompt.trim().length < 10) {
      toast.error("Beschrijving is te kort");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("prompt", prompt);
      if (hwId) fd.append("hardware_id", hwId);
      fd.append("save_to_hardware", String(saveToHardware));
      if (refFile) fd.append("file", refFile);
      const { data } = await api.post("/admin/ai/generate-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data);
      loadHistory();
      toast.success("Afbeelding gegenereerd");
    } catch (e) {
      toast.error("Generatie mislukt: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Verwijder afbeelding?")) return;
    await api.delete(`/admin/ai/generated/${id}`);
    loadHistory();
    if (result?.id === id) setResult(null);
  };

  return (
    <div data-testid="admin-ai-studio">
      <div className="flex items-center gap-3 mb-2">
        <Wand2 className="w-6 h-6 text-[#D97742]" />
        <h2 className="display text-3xl font-semibold">AI Studio <span className="text-sm text-[#A8B3AD] font-normal align-middle ml-2">powered by Gemini Nano Banana</span></h2>
      </div>
      <p className="text-[#A8B3AD] text-sm mb-6 max-w-2xl">
        Genereer foto-realistische hero-afbeeldingen voor je kiosken uit tekst, of upload een referentiefoto om een schonere productrender te krijgen. Resultaten kun je direct als hoofdfoto aan een hardware-product koppelen.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface p-6 space-y-4">
          <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A]">Brief</div>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#6FAA8A]">Koppel aan hardware (optioneel)</span>
            <select className="input-field mt-1" value={hwId} onChange={(e) => fillFromHardware(e.target.value)} data-testid="ai-hardware-select">
              <option value="">— geen —</option>
              {hardware.map((h) => <option key={h.id} value={h.id}>{h.name} ({h.sku})</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#6FAA8A]">Beschrijving</span>
            <textarea className="input-field mt-1 font-mono text-xs" rows={5}
              placeholder="bv. sleek black 21.5 inch freestanding self-order kiosk with integrated payment terminal, mounted in a modern fast-casual restaurant interior"
              value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ai-prompt-input" />
          </label>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#6FAA8A]">Referentiefoto (optioneel)</span>
            <div className="mt-1 flex items-center gap-3 border border-dashed border-[#2E3E39] rounded-lg p-4 hover:border-[#6FAA8A] transition">
              <ImagePlus className="w-5 h-5 text-[#6FAA8A]" />
              <input type="file" accept="image/*" onChange={(e) => setRefFile(e.target.files?.[0] || null)} data-testid="ai-reference-input" />
              {refFile && <span className="text-xs text-[#F4F1EA] ml-auto truncate max-w-[150px]">{refFile.name}</span>}
            </div>
          </label>

          {hwId && (
            <label className="flex items-center gap-2 text-sm text-[#F4F1EA]">
              <input type="checkbox" checked={saveToHardware}
                onChange={(e) => setSaveToHardware(e.target.checked)}
                data-testid="ai-save-to-hardware-checkbox" />
              Instellen als hoofdfoto van dit product
            </label>
          )}

          <button className="btn-accent w-full justify-center" onClick={generate} disabled={busy} data-testid="ai-generate-button">
            {busy ? <><Sparkles className="w-4 h-4 animate-pulse" /> Genereren… (15-30s)</> : <><Wand2 className="w-4 h-4" /> Genereer hero-afbeelding</>}
          </button>
        </div>

        <div className="card-surface p-6 min-h-[420px] grid place-items-center product-plate">
          {busy && (
            <div className="text-center">
              <Sparkles className="w-10 h-10 text-[#D97742] mx-auto animate-pulse" />
              <div className="mt-4 text-sm text-[#A8B3AD]">Nano Banana genereert je afbeelding…</div>
            </div>
          )}
          {!busy && !result && (
            <div className="text-center text-[#6A7672]">
              <ImagePlus className="w-12 h-12 mx-auto opacity-40" />
              <div className="mt-3 text-sm">Resultaat verschijnt hier</div>
            </div>
          )}
          {!busy && result && (
            <div className="w-full text-center">
              <img src={`${BASE}${result.url_path}`} alt="generated" className="max-h-[360px] max-w-full mx-auto object-contain rounded-lg" data-testid="ai-result-image" />
              <div className="mt-4 flex items-center justify-center gap-2">
                <a className="btn-outline !py-2" href={`${BASE}${result.url_path}`} download={result.filename} data-testid="ai-download-button">
                  <Download className="w-4 h-4" /> Download
                </a>
                <button className="btn-ghost text-[#FF6B5B] !py-2" onClick={() => del(result.id)} data-testid="ai-delete-result">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="mt-10">
          <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A] mb-4">Eerder gegenereerd</div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {history.map((h) => (
              <div key={h.id} className="card-interactive p-2 relative group" data-testid={`ai-history-${h.id}`}>
                <img src={`${BASE}${h.url_path}`} alt="" className="w-full h-32 object-contain rounded-md bg-[#0F1613]" />
                <div className="text-[10px] text-[#A8B3AD] mt-2 truncate" title={h.prompt}>{h.prompt.slice(0, 60)}</div>
                <button className="absolute top-2 right-2 bg-[#0B110F]/80 text-[#FF6B5B] rounded-full w-7 h-7 grid place-items-center opacity-0 group-hover:opacity-100 transition"
                  onClick={() => del(h.id)} data-testid={`ai-history-delete-${h.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

